import { NamingConvention } from 'breeze-client';
import * as _ from 'lodash-es';

export type ValueCustomizer = (obj: any, key: string, value: any) => any | undefined;

export interface DateHandling {
  // If true, UTC dates are parsed as local dates
  parseUtcAsLocalDate?: boolean;
}

export interface MappingOptions {
  customizer?: ValueCustomizer;
  dateHandling?: DateHandling;
  parseStringAsNumber?: boolean;
}

export const setGlobalDateHandling = (dateHandling: DateHandling): void => {
  globalDateHandling = dateHandling;
};

let globalDateHandling: DateHandling = {};

/**
 * Maps the keys and values of a JSON object. Keys will be converted to PamelCase and values will be converted if necessary

 * @param obj
 * @param options
 */
export const mapJsonObjectToServer = <T extends Record<string, any>>(obj: T, options?: MappingOptions): T => {
  options = {
    dateHandling: globalDateHandling || {},
    ...(options || {}),
  };
  const customizer = options.customizer;

  if (!obj) {
    return obj;
  }

  obj = (obj as any).$values || obj;
  if (Array.isArray(obj)) {
    return mapValueToServer(obj, {
      ...options,
      customizer: (_obj, key, value) => {
        if (customizer) {
          const v = customizer(_obj, key, value);
          if (v === undefined) {
            return mapValueToServer(value, options);
          }
          return v;
        }
        return mapValueToServer(value, options);
      },
    });
  }

  obj = _.mapKeys(obj, (value, key) => {
    // Handle abbreviations, e.g. ABN, GSTStatus
    key = key.replace(/^([^a-z]+)([A-Z][a-z]|$)/, (match, p1: string, p2: string) => `${p1.toLowerCase()}${p2}`);

    return NamingConvention.defaultInstance.clientPropertyNameToServer(key);
  }) as T;
  obj = _.mapValues(obj, (value, key) => {
    if (customizer) {
      const v = customizer(obj, key, value);
      if (v === undefined) {
        return mapValueToServer(value, options);
      }
      return v;
    }
    return mapValueToServer(value, options);
  }) as T;
  return obj;
};

/**
 * Maps the keys and values of a JSON object. Keys will be converted to camelCase and values will be parsed

 * @param obj
 * @param options
 */
export const mapJsonObjectToClient = <T extends Record<string, any>>(obj: T, options?: MappingOptions): T => {
  options = {
    dateHandling: globalDateHandling || {},
    ...(options || {}),
  };
  const customizer = options.customizer;

  if (!obj) {
    return obj;
  }

  obj = (obj as any).$values || obj;
  if (Array.isArray(obj)) {
    return mapValueToClient(obj, {
      ...options,
      customizer: (_obj, key, value) => {
        if (customizer) {
          const v = customizer(_obj, key, value);
          if (v === undefined) {
            return mapValueToClient(value, options);
          }
          return v;
        }
        return mapValueToClient(value, options);
      },
    });
  }

  if (typeof obj !== 'object') {
    return mapValueToClient(obj, options);
  }

  obj = _.mapKeys(obj, (value, key) => {
    // Handle abbreviations, e.g. ABN, GSTStatus
    key = key.replace(/^([^a-z]+)([A-Z][a-z]|$)/, (match, p1: string, p2: string) => `${p1.toLowerCase()}${p2}`);

    return NamingConvention.defaultInstance.serverPropertyNameToClient(key);
  }) as T;
  obj = _.mapValues(obj, (value, key) => {
    if (customizer) {
      const v = customizer(obj, key, value);
      if (v === undefined) {
        return mapValueToClient(value, options);
      }
      return v;
    }
    return mapValueToClient(value, options);
  }) as T;
  return obj;
};

const mapValueToClient = (value: any, options: MappingOptions): any => {
  if (value === null || value === undefined) {
    return value;
  }

  if (typeof value === 'string') {
    return (
      tryParseBool(value) ||
      tryParseNumber(value, options.parseStringAsNumber ?? false) ||
      tryParseDate(value, options.dateHandling) ||
      value
    );
  }

  value = value.$values || value;
  if (Array.isArray(value)) {
    return value.map((item) => mapValueToClient(item, options));
  }

  if (typeof value === 'object') {
    return mapJsonObjectToClient(value, options);
  }

  return value;
};

export const mapValueToServer = (value: any, options?: MappingOptions): any => {
  if (value === null || value === undefined) {
    return value;
  }

  if (value instanceof Date) {
    const dateHandling = options?.dateHandling || globalDateHandling;
    if (dateHandling?.parseUtcAsLocalDate) {
      return new Date(value.getTime() - value.getTimezoneOffset() * 60000);
    }
  }

  value = value.$values || value;
  if (Array.isArray(value)) {
    return value.map((item) => mapValueToServer(item, options));
  }

  if (typeof value === 'object') {
    return mapJsonObjectToServer(value, options);
  }

  return value;
};

/**
 * Attempts to parse a string as a Date and if a UTC date, shifts it to display correctly as a local date
 * @param value string to be parsed
 * @returns Null or Date object
 */
export const tryParseDate = (value: string, dateHandling?: DateHandling): Date => {
  dateHandling = dateHandling || globalDateHandling;
  const parseDate = (dateStr: string): Date => {
    const regEx =
      // eslint-disable-next-line max-len
      /^([\+-]?\d{4}(?!\d{2}\b))((-?)((0[1-9]|1[0-2])(\3([12]\d|0[1-9]|3[01]))?|W([0-4]\d|5[0-2])(-?[1-7])?|(00[1-9]|0[1-9]\d|[12]\d{2}|3([0-5]\d|6[1-6])))([T\s]((([01]\d|2[0-3])((:?)[0-5]\d)?|24\:?00)([\.,]\d+(?!:))?)?(\17[0-5]\d([\.,]\d+)?)?([zZ]|([\+-])([01]\d|2[0-3]):?([0-5]\d)?)?)?)?$/;
    return regEx.test(dateStr) ? new Date(dateStr) : null;
  };

  if (typeof value === 'string') {
    let date = parseDate(value);
    // Strip Zulu and parse as local date
    if (date && dateHandling?.parseUtcAsLocalDate && /Z$/.test(value)) {
      date = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
    }

    return date;
  }

  return null;
};

export const tryParseBool = (value: string): boolean => {
  if (value.toLowerCase() === 'true') {
    return true;
  }
  if (value.toLowerCase() === 'false') {
    return false;
  }
  return null;
};

export const tryParseNumber = (value: string, parseStringAsnumber: boolean): string | number => {
  // Checks if string can be parsed to a number, but doesn't actually return the parsed number
  // This is to ensure, the numbers are not accidentially parsed as dates later in the chain
  const num = +value;
  return isFinite(num) ? (parseStringAsnumber ? num : value) : null;
};
